import { useEffect } from "react";
import { faArrowRight, faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";

import { parseNumber } from "common/utils/numbers";
import { useMatch } from "common/router/hooks";
import Icon from "common/components/Icon";
import { PromotionKind } from "common/types/promotions";
import { useListSearchState } from "common/hooks";

import Page from "client/common/components/layouts/Page";
import Body from "client/common/components/layouts/Body";
import Container from "client/common/components/layouts/Container";
import FetchErrorPage from "client/common/components/errors/FetchErrorPage";
import Results from "client/common/components/results/Results";
import LoadingPage from "client/common/components/loading/LoadingPage";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import PillButton from "client/common/components/buttons/PillButton";
import { useClinic, useFeatures } from "client/clinics/hooks";
import { INITIAL_CLIENT_DATA } from "client/common/utils";
import { Paths } from "client/routes";
import { ProductListSource } from "client/analytics/types";
import HomeBrowseV1, { CategoryCarousel } from "client/home/components/HomeBrowseV1";
import HomeBrowseV2, { CategoryCarouselV2 } from "client/home/components/HomeBrowseV2";

import {
  EMPTY_PRODUCT_SELECTION,
  getPromoCodeMessagePrefix,
  getPromoCodePunctuation,
  getPromotionPageStatus,
  getProductFiltersFromSearch,
  partitionCarousels,
  toPromotionDateRange,
} from "../utils";
import {
  useBrowseQuery,
  useCarouselDisplayQuery,
  useIncentiveProductsQuery,
  useIncentiveQuery,
  useRebateProductsQuery,
  useRebateQuery,
} from "../hooks";
import { BrowseAllPatientKind } from "../types";
import { DEFAULT_PAGE_SIZE } from "../constants/filterDefinitions";
import ProductCardResults from "./ProductCardResults";
import PreFooter from "./PreFooter";
import { usePromotionDrawer } from "../stackables";

import styles from "../styles/PromotionPage.module.less";

// The incentive and rebate routes share their params and search.
type PromotionPath = Paths["products"]["incentive"];

const INITIAL_SEARCH = {
  ...EMPTY_PRODUCT_SELECTION,
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
};

type PromotionPageProps = {
  kind: PromotionKind;
};

/**
 * The page for one incentive or rebate, reached from promotional emails and the promotion
 * drawer: its terms as a hero, then every product it covers as product cards.
 */
const PromotionPage = ({ kind }: PromotionPageProps) => {
  const clinic = useClinic();
  const features = useFeatures();
  const isCarouselV2 = features.CAROUSEL_V2;
  const HomeBrowse = isCarouselV2 ? HomeBrowseV2 : HomeBrowseV1;
  const isRebate = kind === PromotionKind.Rebate;
  const { is_staff: isStaff } = INITIAL_CLIENT_DATA;

  const match = useMatch<PromotionPath>();
  const parsedId = parseNumber(match.params.id, 0);
  const [search, updateSearch] = useListSearchState<PromotionPath>(INITIAL_SEARCH);
  const filters = getProductFiltersFromSearch(search);
  const { open: openPromotionDrawer } = usePromotionDrawer();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  const incentiveQuery = useIncentiveQuery(parsedId, { enabled: !isRebate });
  const rebateQuery = useRebateQuery(parsedId, { enabled: isRebate });
  const {
    data: promotion,
    error: promotionError,
    isFetching: isPromotionFetching,
    isLoading: isPromotionLoading,
  } = isRebate ? rebateQuery : incentiveQuery;

  const productsSearch = {
    ...filters,
    page_size: DEFAULT_PAGE_SIZE,
    page: parseNumber(search.page, 1),
  };
  const incentiveProductsQuery = useIncentiveProductsQuery(parsedId, productsSearch, {
    enabled: !isRebate,
  });
  const rebateProductsQuery = useRebateProductsQuery(parsedId, productsSearch, {
    enabled: isRebate,
  });
  const {
    data: products,
    error: productsError,
    isFetching: areProductsFetching,
    isLoading: areProductsLoading,
  } = isRebate ? rebateProductsQuery : incentiveProductsQuery;

  const {
    data: browse,
    isLoading: isBrowseLoading,
    error: browseError,
  } = useBrowseQuery({ species: BrowseAllPatientKind.ALL }, { enabled: !isCarouselV2 });
  const {
    data: carouselDisplay,
    isLoading: isCarouselDisplayLoading,
    error: carouselDisplayError,
  } = useCarouselDisplayQuery({ species: BrowseAllPatientKind.ALL }, { enabled: isCarouselV2 });

  const renderResults = () => {
    if (areProductsLoading) {
      return <Results.Loading description="Loading products" />;
    }
    if (productsError || !products) {
      return <Results.FetchError error={productsError} entityName="product" />;
    }
    return (
      <ProductCardResults
        products={products}
        isRefetching={isPromotionFetching || areProductsFetching}
        filters={filters}
        onFiltersChange={updateSearch}
        onPageChange={(page) => updateSearch({ page })}
        listSource={isRebate ? ProductListSource.RebatePage : ProductListSource.IncentivePage}
        listContext={{ promotionId: parsedId }}
        entityName="product"
        className={styles.content}
      />
    );
  };

  const renderCarousel = () => {
    if (isCarouselV2) {
      if (isCarouselDisplayLoading) {
        return <LoadingContainer />;
      }
      if (carouselDisplayError || !carouselDisplay) {
        return null;
      }
      return (
        <CategoryCarouselV2
          carousels={partitionCarousels(carouselDisplay.carousels).categoryCarousels}
        />
      );
    }
    if (isBrowseLoading) {
      return <LoadingContainer />;
    }
    if (browseError || !browse) {
      return null;
    }
    return <CategoryCarousel categories={browse.categories} />;
  };

  if (isPromotionLoading) {
    return <LoadingPage />;
  }

  if (!promotion || promotionError) {
    return <FetchErrorPage error={promotionError} entityName={isRebate ? "rebate" : "incentive"} />;
  }

  const status = getPromotionPageStatus(promotion, isStaff);

  if (status !== "live") {
    const errorMessage = `This ${promotion.promo_codes.length ? "promo code" : "discount"} ${
      status === "expired" ? "has expired" : "is not active"
    }`;

    return (
      <Page>
        <div className={styles.gradient} />
        <Container>
          <div className={styles.top}>
            <div className={styles.heading}>
              <div className={styles.title}>Uh oh! {errorMessage}...</div>
              <div className={styles.subtitle}>
                You can still browse products for your furry friend. Shop preventatives, diet, and
                medications below!
              </div>
            </div>
          </div>
        </Container>
        <Body className={styles.main}>
          <HomeBrowse clinic={clinic} />
        </Body>
      </Page>
    );
  }

  const bannerImageUrl = "banner_image_url" in promotion ? promotion.banner_image_url : null;

  const renderPromoCodeMessage = () => {
    const codes = promotion.promo_codes.map((promo) => promo.code);
    const codePunctuation = getPromoCodePunctuation(codes);
    const messagePrefix = getPromoCodeMessagePrefix(promotion.promo_message);
    return (
      <p className={styles.promoCodeMessage}>
        {messagePrefix} Use promo code{" "}
        {codes.map((code) => (
          <span className={styles.promoCodeContainer} key={code}>
            {codePunctuation[code]}
            <span className={styles.promoCode}>{code}</span>
          </span>
        ))}{" "}
        at checkout.
      </p>
    );
  };

  return (
    <Page>
      {!!bannerImageUrl && (
        <div className={styles.background} style={{ backgroundImage: `url(${bannerImageUrl})` }} />
      )}
      <div className={cx(styles.gradient, bannerImageUrl && styles.gradientOverBanner)} />
      <Container>
        <div className={styles.top}>
          <div className={styles.heading}>
            <div className={styles.expiration}>
              {toPromotionDateRange(promotion.start_date, promotion.end_date)}
            </div>
            <div className={styles.titleRowContainer}>
              <div className={styles.title}>{promotion.promo_title}</div>
              <PillButton
                className={styles.button}
                variant="primary"
                onClick={() =>
                  openPromotionDrawer({ kind, id: promotion.id, shouldDisplayItems: false })
                }
              >
                <div className={styles.buttonContent}>
                  <Icon icon={faInfoCircle} />
                  <div>View details</div>
                  <Icon className={styles.angleRight} icon={faArrowRight} />
                </div>
              </PillButton>
            </div>
            <div className={styles.subtitle}>
              {promotion.promo_message}
              {!!promotion.promo_codes.length && renderPromoCodeMessage()}
            </div>
          </div>
        </div>
      </Container>
      <div>
        <Body className={styles.main}>{renderResults()}</Body>
        <Body className={styles.browseBody}>
          <div className={styles.browse}>
            <h3 className={styles.browseTitle}>
              Browse our online store{" "}
              <span className={styles.accent}>& explore other top veterinarian picks</span>
            </h3>
            {renderCarousel()}
          </div>
        </Body>
      </div>
      <PreFooter
        image={
          ("prefooter_image_url" in promotion && promotion.prefooter_image_url) ||
          clinic.storefront_prefooter_image
        }
      />
    </Page>
  );
};

export default PromotionPage;
